import {
    createContext,
    useContext,
    useEffect,
    useState,
    type ReactNode,
} from "react";
import {
    type Locale,
    DEFAULT_LOCALE,
    detectLocale,
    persistLocale,
} from "./config";
import { en } from "./dictionaries/en";
import { es } from "./dictionaries/es";
import { ptBR } from "./dictionaries/pt-BR";
import type { TranslationDictionary } from "./types";

const DICTIONARIES: Record<Locale, TranslationDictionary> = {
    en,
    es,
    "pt-BR": ptBR,
};

type Vars = Record<string, string | number>;

interface LocaleContextType {
    locale: Locale;
    setLocale: (locale: Locale) => void;
    t: <NS extends keyof TranslationDictionary, K extends keyof TranslationDictionary[NS]>(
        ns: NS,
        key: K,
        vars?: Vars,
    ) => string;
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);

    // Client-only: read from localStorage / browser on mount.
    // Must be in useEffect (not useState initializer) to avoid SSR hydration mismatch.
    useEffect(() => {
        setLocaleState(detectLocale()); // eslint-disable-line react-hooks/set-state-in-effect
    }, []);

    // Keep <html lang> correct for accessibility
    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    const setLocale = (next: Locale) => {
        persistLocale(next);
        setLocaleState(next);
    };

    const t = <
        NS extends keyof TranslationDictionary,
        K extends keyof TranslationDictionary[NS],
    >(
        ns: NS,
        key: K,
        vars?: Vars,
    ): string => {
        const dict = DICTIONARIES[locale];
        // Fall back to English if a key is missing in the current locale
        let str = ((dict[ns][key] ?? en[ns][key]) as string | undefined) ?? String(key);
        if (vars) {
            for (const [k, v] of Object.entries(vars)) {
                str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
            }
        }
        return str;
    };

    return (
        <LocaleContext.Provider value={{ locale, setLocale, t }}>
            {children}
        </LocaleContext.Provider>
    );
}

export function useLocale(): LocaleContextType {
    const ctx = useContext(LocaleContext);
    if (!ctx) throw new Error("useLocale must be used within a LocaleProvider");
    return ctx;
}

/** Alias: const { t } = useTranslation() */
export const useTranslation = useLocale;

/** Pluralise using Intl.PluralRules */
export function plural(
    count: number,
    locale: Locale,
    one: string,
    other: string,
): string {
    return new Intl.PluralRules(locale).select(count) === "one" ? one : other;
}
